import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
	fetchCategories,
	fetchProviders,
	fetchInstitutionsList,
	fetchTagsList
} from 'store/actions';

// Import defaultValues
import { defaultFieldValues } from './fields/input_fields';

// Import translation
import { useTranslation } from 'react-i18next';

// Import utilities
import { submitForm } from 'components/utilities/form';

// Validate
import validate from './validation';

// Import components
import { FormTemplate } from 'components/common/templates';
import { FormButtons } from 'components/common/buttons';
import Fields from './fields/Fields';
import ProductizationNote from 'components/utilities/info/ProductizationNote';

// Import helpers
import {
	vodCategoriesOptions,
	convertDataToNames,
	convertToIds
} from 'components/helpers/genres_helpers/helpers';
import { convertInstitutionsToValuePairs } from 'components/helpers/institutions_helpers/helpers';
import { convertProvidersToId } from 'components/views/vod/helpers/helpers';
import { convertDurationTime } from 'components/helpers/convert';

// Import variables
import { DURATION_CONVERT_TYPES } from 'components/helpers/variables';

// Import hooks
import useAudioLocation from 'hooks/useAudioLocation';

const { HMS_TO_SECONDS } = DURATION_CONVERT_TYPES;

const VodCreateForm = ({
	history,
	fetchCategories,
	fetchProviders,
	genresList,
	providersList,
	paymentsModelsList,
	fetchInstitutionsList,
	institutionsList,
	match: {
		params: { id }
	},
	fetchTagsList,
	tagsList
}) => {
	useEffect(() => {
		if (id) localStorage.setItem('series', id);
		// Fetch categories with default params to feed genres combobox
		fetchCategories(vodCategoriesOptions, null, null);
		fetchProviders();
		// Fetch tags list
		fetchTagsList();
		// eslint-disable-next-line
	}, []);

	const { t } = useTranslation();

	const { isAudio } = useAudioLocation();

	const handleOnSubmit = ({ isEdit, itemID }) => async (values) => {
		const {
			genres,
			subtype,
			products_group_uuid,
			is_audio,
			tags,
			metadata
		} = values;

		// selected genres mapped to array of ids
		const genresId = convertToIds(genresList, genres);

		// selected tags mapped to array of ids
		const tagsId = convertToIds(tagsList, tags);

		// convert duration from sec to min
		const durationInSeconds = convertDurationTime({
			duration: metadata.duration,
			type: HMS_TO_SECONDS
		});

		// resources to submit
		const resources = {
			...values,
			metadata: {
				...metadata,
				duration: durationInSeconds,
				sub_category: metadata.sub_category,
				rating: parseInt(metadata.rating, 10),
				additional_metadata: metadata.additional_metadata || [],
				title_org: metadata.title
			},
			genres: genresId,
			products_group_uuid: products_group_uuid || null,
			tags: tagsId,
			is_audio: parseInt(is_audio, 10)
		};

		const api = subtype === 'series' ? `vod/${subtype}` : 'vod';
		const editBasePath = `/panel/${is_audio ? 'audio' : 'vod'}/${subtype}`;

		// Submit the form with field values
		return await submitForm({
			history,
			isEdit,
			itemID,
			resources,
			api,
			editBasePath
		});
	};

	// array of genres names that will be passed to multi-select
	// both initial values and data for combobox
	const genresListNames = convertDataToNames(genresList);

	// array of tags names that will be passed to multi-select
	// both initial values and data for combobox
	const tagsListNames = convertDataToNames(tagsList);

	// arrays of provider id's passed to suggestion combobox for form fields
	const providersId = convertProvidersToId(providersList);

	const institutionsNames = convertInstitutionsToValuePairs(institutionsList);

	const formInitialValues = {
		...defaultFieldValues,
		products_group_uuid: institutionsNames[0]?.value,
		is_audio: isAudio ? 1 : 0
	};

	return (
		// Dispatch fetchSeries actions in series_form
		<FormTemplate fetchFormResources={[fetchInstitutionsList]}>
			{({ isEdit, itemID, checkedPermissions, decorators }) => (
				<>
					<ProductizationNote />
					<Form
						initialValues={formInitialValues}
						validate={validate}
						onSubmit={handleOnSubmit({ isEdit, itemID })}
						decorators={decorators}
						render={({
							handleSubmit,
							submitting,
							values,
							form: { change }
						}) => (
							<form onSubmit={handleSubmit}>
								<Fields
									currentValues={values}
									isEdit={isEdit}
									submitting={submitting}
									changeFieldValue={change}
									genres={genresListNames}
									providersId={providersId}
									paymentsModelsList={paymentsModelsList}
									institutions={institutionsNames}
									checkedPermissions={checkedPermissions}
									tags={tagsListNames}
								/>
								<FormButtons
									isButtonDisabled={submitting}
									path="vod"
									buttonText={t('common:buttons.submit')}
								/>
							</form>
						)}
					/>
				</>
			)}
		</FormTemplate>
	);
};

VodCreateForm.propTypes = {
	history: PropTypes.object.isRequired,
	fetchCategories: PropTypes.func.isRequired,
	fetchProviders: PropTypes.func.isRequired,
	genresList: PropTypes.array.isRequired,
	providersList: PropTypes.array.isRequired,
	paymentsModelsList: PropTypes.array.isRequired,
	institutionsList: PropTypes.array.isRequired,
	fetchInstitutionsList: PropTypes.func.isRequired,
	match: PropTypes.shape({
		params: PropTypes.shape({ id: PropTypes.string })
	}),
	fetchTagsList: PropTypes.func.isRequired
};

const mapStateToProps = ({
	categories: {
		table: { data }
	},

	providers,
	dictionary
}) => ({
	genresList: data,
	providersList: providers.table.data,
	paymentsModelsList: dictionary.paymentModels,
	institutionsList: dictionary.institutionsList,
	tagsList: dictionary.tagsList
});

export default compose(
	connect(mapStateToProps, {
		fetchCategories,
		fetchProviders,
		fetchInstitutionsList,
		fetchTagsList
	}),
	withRouter
)(VodCreateForm);
